import React, { useState, useEffect } from 'react';
import { Row, Col, Form } from 'react-bootstrap';

interface ViewOptionsProps {
	store: any;
	svgToURL: (template: string) => string;
	unitToPx: (params: { unit: string; dpi: number; unitVal: number }) => number;
	pxToUnit: (params: { px: number; unit: string; dpi: number }) => number;
	t: (key: string) => string;
}

// define the new custom section
export const ViewOptions: React.FC<ViewOptionsProps> = ({ store, svgToURL, unitToPx, pxToUnit, t }) => {
	const [unit] = useState(store.unit);
	const [dpi] = useState(store.dpi);

	// Page size controls (in pixels)
	// NOTE: Commented out for now; will revisit later
	// const [pageWidth, setPageWidth] = useState(
	// 	store.activePage?.computedWidth ?? store.width,
	// );
	// const [pageHeight, setPageHeight] = useState(
	// 	store.activePage?.computedHeight ?? store.height,
	// );

	// NOTE: Can NOT be less than 1/2 in or 0.5 in
	// will cause "Maximum update depth exceeded" error
	const [gridVisible, setGridVisible] = useState(
		store.activePage?.gridSize > 0 ? true : false,
	);
	const [gridSize, setGridSize] = useState(
		store.activePage?.gridSize > 0 ? store.activePage?.gridSize : 0.5,
	);

	const [marginVisible, setMarginVisible] = useState(
		store.activePage?.margin > 0 ? true : false,
	);
	const [margin, setMargin] = useState(
		store.activePage?.margin > 0 ? store.activePage?.margin : 0.5,
	);

	const [bleedVisible, setBleedVisible] = useState(
		store.activePage?.bleed > 0 ? true : false,
	);

	const [bleed, setBleed] = useState(
		store.activePage?.bleed > 0
			? Math.fround(
					pxToUnit({
						px: store.activePage.bleed,
						unit: 'in',
						dpi: store.dpi,
					}),
				)
			: 0.125,
	);

	// function to remove all grid-related elements
	// we also can hide them instead
	// but I think removing is just quicker
	const hideGridLines = () => {
		const gridElements = store.activePage.children.filter(
			(child: any) => child.name === 'gridLines',
		);
		const ids = gridElements.map((el: any) => el.id);
		store.deleteElements(ids);
		store.activePage.set({
			gridSize: 0,
		});
	};

	// "dot" is a fake svg elements with no content
	// but polotno will use it as snap point
	const createDot = (x: number, y: number) => {
		const template =
			'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"></svg>';
		const url = svgToURL(template);

		store.activePage.addElement({
			x,
			y,
			type: 'svg',
			width: 1,
			height: 1,
			src: url,
			name: 'gridLines',
			selectable: false, // Ignored if role is set to 'admin'
		});
	};

	const setGridLines = (gridSizeValue: number) => {
		hideGridLines();
		const width = store.activePage?.computedWidth ?? store.width;
		const height = store.activePage?.computedHeight ?? store.height;

		const gridInPx = unitToPx({
			unit: 'in',
			dpi,
			unitVal: typeof gridSizeValue === 'number' ? gridSizeValue : gridSize,
		});

		const rows = Math.floor(height / gridInPx);
		const cols = Math.floor(width / gridInPx);

		const colLines = [...Array(cols)].map((_, index) => {
			const x = (index + 1) * gridInPx;

			createDot(x, 0);

			return `<line x1="${x}" y1="0" x2="${x}" y2="${height}" stroke="blue" stroke-width="5" stroke-dasharray="10"/>`;
		});

		const rowLines = [...Array(rows)].map((_, index) => {
			const y = (index + 1) * gridInPx;

			createDot(0, y);

			return `<line x1="0" y1="${y}" x2="${width}" y2="${y}" stroke="blue" stroke-width="5" stroke-dasharray="10"/>`;
		});

		// add grid image into the page
		const url = svgToURL(
			`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">${rowLines.join('')}${colLines.join('')}</svg>`,
		);

		store.activePage.set({
			gridSize: typeof gridSizeValue === 'number' ? gridSizeValue : gridSize,
		});

		store.activePage.addElement({
			type: 'svg',
			width,
			height,
			src: url,
			name: 'gridLines',
			selectable: false, // Ignored if role is set to 'admin'
			opacity: 0.2,
			alwaysOnTop: false,
		});
	};

	const hideMarginLines = () => {
		// remove old lines
		const lineElements = store.activePage.children.filter(
			(child: any) => child.name === 'marginLines',
		);
		const ids = lineElements.map((el: any) => el.id);
		store.deleteElements(ids);
		store.activePage.set({
			margin: 0,
		});
	};

	const showMarginLines = (marginAmount: number) => {
		hideMarginLines();

		const width = store.activePage?.computedWidth ?? store.width;
		const height = store.activePage?.computedHeight ?? store.height;

		const m = unitToPx({
			unit: 'in',
			dpi: store.dpi,
			unitVal: marginAmount ?? margin,
		});

		// generate svg data for grid image
		const template = `
			<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">
				<line x1="0" y1="${m}" x2="${width}" y2="${m}" stroke="red" stroke-width="5" stroke-dasharray="25"/>
				<line x1="${m}" y1="${0}" x2="${m}" y2="${height}" stroke="red" stroke-width="5" stroke-dasharray="25"/>
				<line x1="${width - m}" y1="${0}" x2="${width - m}" y2="${height}" stroke="red" stroke-width="5" stroke-dasharray="25"/>
				<line x1="0" y1="${height - m}" x2="${width}" y2="${height - m}" stroke="red" stroke-width="5" stroke-dasharray="25"/>
			</svg>`;

		// add grid image into the page
		const url = svgToURL(template);

		store.activePage.set({
			margin: marginAmount ?? margin,
		});

		store.activePage.addElement({
			type: 'svg',
			width,
			height,
			src: url,
			name: 'marginLines',
			selectable: false, // Ignored if role is set to 'admin'
			opacity: 0.5,
			alwaysOnTop: true,
		});
	};

	const toggleBleedArea = (value: boolean) => {
		if (value) {
			store.activePage.set({
				bleed: unitToPx({
					unit: 'in',
					dpi: store.dpi,
					unitVal: bleed,
				}),
			});
			setBleedVisible(value);
			store.toggleBleed(value);
		} else {
			store.activePage.set({
				bleed: 0,
			});
			setBleedVisible(value);
			store.toggleBleed(value);
		}
	};

	const setAmount = (settingType: string, value: string | number) => {
		const numValue = typeof value === 'string' ? parseFloat(value) : value;

		switch (settingType) {
			// NOTE: Can NOT be less than 1/2 in or 0.5 in
			// will cause "Maximum update depth exceeded" error
			case 'grid':
				setGridSize(numValue > 0 ? numValue : 0.5);
				break;
			case 'margin':
				setMargin(numValue);
				break;
			case 'bleed':
				store.activePage.set({
					bleed: unitToPx({
						unit: 'in',
						dpi: store.dpi,
						unitVal: parseFloat((numValue > 0 ? numValue : 0.125).toString()),
					}),
				});

				setBleed(numValue > 0 ? numValue : 0.125);
				break;
			default:
				break;
		}
	};

	useEffect(() => {
		if (gridVisible === true) {
			setGridLines(gridSize);
		} else {
			hideGridLines();
		}
	}, [gridVisible, gridSize]);

	useEffect(() => {
		if (marginVisible === true) {
			showMarginLines(margin);
		} else {
			hideMarginLines();
		}
	}, [marginVisible, margin]);

	useEffect(() => {
		store.setUnit({
			unit,
			dpi: parseInt(dpi),
		});
	}, [unit, dpi, store]);

	// Keep local size state in sync when active page changes
	// useEffect(() => {
	// 	setPageWidth(store.activePage?.computedWidth ?? store.width);
	// 	setPageHeight(store.activePage?.computedHeight ?? store.height);
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, [store.activePage]);

	// const clampSize = (val) => {
	// 	const num = Number(val);
	// 	if (Number.isNaN(num)) return 100;
	// 	return Math.max(100, Math.floor(num));
	// };

	// const applyPageSize = (w, h) => {
	// 	const newW = clampSize(w);
	// 	const newH = clampSize(h);
	// 	// Resize just the active page; useMagic scales elements proportionally
	// 	store.activePage?.setSize({ width: newW, height: newH, useMagic: true });
	// 	setPageWidth(newW);
	// 	setPageHeight(newH);
	// };

	// const handleAddPage = () => {
	// 	// Limit to max 10 pages for testing as requested
	// 	if ((store.pages?.length ?? 0) >= 10) return;
	// 	const newPage = store.addPage({ bleed: store.activePage?.bleed || 0 });
	// 	// Insert after current page
	// 	const index = store.pages.indexOf(store.activePage);
	// 	newPage.setZIndex(index + 1);
	// };

	return (
		<Form id="variablesForm" style={{ minWidth: '300px' }}>
			{/* Pages controls (Add/Resize) temporarily commented out for later work */}
			{/*
			<Row className="mb-3">
				<Col>
					<div className="d-flex align-items-center justify-content-between py-2">
						<div className="fw-bold">{t?.('pages') || 'Pages'}</div>
					</div>
					<div className="text-700 fs-7 mb-2">{(store.pages?.length ?? 0)}/10</div>
					<div className="row g-3">
						<div className="col-6">
							<Form.Group>
								<Form.Label>{t?.('width') || 'Width'} (px)</Form.Label>
							</Form.Group>
						</div>
						<div className="col-6">
							<Form.Group>
								<Form.Label>{t?.('height') || 'Height'} (px)</Form.Label>
							</Form.Group>
						</div>
					</div>
				</Col>
			</Row>
			*/}
			<Row className="mb-3">
				<Col>
					<Form.Group className="py-3">
						<Form.Label className="cursor-pointer" htmlFor="grid_switch">
							{t('gridSwitchLabel')}
						</Form.Label>
						<Form.Check
							checked={gridVisible}
							id="grid_switch"
							label={`${t('grid')} ${gridVisible ? t('on') : t('off')}`}
							type="switch"
							onChange={(e) => setGridVisible(e.target.checked)}
						/>
					</Form.Group>
					{gridVisible && (
						<Form.Group className="py-3">
							<Form.Label>{t('sizeInches')}</Form.Label>

							<Form.Control
								aria-label="Grid Size"
								max={1}
								min={0.5}
								placeholder="0.5"
								step={0.125}
								type="number"
								value={gridSize}
								onChange={(e) => setAmount('grid', e.target.value)}
							/>

							<Form.Range
								aria-label="Grid Size"
								className="mt-3"
								max={1}
								min={0.5}
								step={0.125}
								value={gridSize}
								onChange={(e) => setAmount('grid', e.target.value)}
							/>
						</Form.Group>
					)}
					<Form.Group className="py-3">
						<Form.Label className="cursor-pointer" htmlFor="margin_switch">
							{t('marginSwitchLabel')}
						</Form.Label>
						<Form.Check
							checked={marginVisible}
							id="margin_switch"
							label={`${t('margin')} ${marginVisible ? t('on') : t('off')}`}
							type="switch"
							onChange={(e) => setMarginVisible(e.target.checked)}
						/>
					</Form.Group>
					{marginVisible && (
						<Form.Group className="py-3">
							<Form.Label>{t('sizeInches')}</Form.Label>

							<Form.Control
								aria-label="Safe Area Size"
								max={1}
								min={0.125}
								placeholder="0.125"
								step={0.125}
								type="number"
								value={margin}
								onChange={(e) => setAmount('margin', e.target.value)}
							/>

							<Form.Range
								aria-label="Margin Size"
								className="mt-3"
								max={1}
								min={0.125}
								step={0.125}
								value={margin}
								onChange={(e) => setAmount('margin', e.target.value)}
							/>
						</Form.Group>
					)}
					<Form.Group className="py-3">
						<Form.Label className="cursor-pointer" htmlFor="bleed_switch">
							{t('bleedSwitchLabel')}
						</Form.Label>
						<Form.Check
							checked={bleedVisible}
							id="bleed_switch"
							label={`${t('bleed')}	${bleedVisible ? t('on') : t('off')}`}
							type="switch"
							onChange={(e) => toggleBleedArea(e.target.checked)}
						/>
					</Form.Group>
					{bleedVisible && (
						<Form.Group className="py-3" controlId="formBasicEmail">
							<Form.Label>{t('sizeInches')}</Form.Label>
							<Form.Control
								aria-label="Bleed Size"
								max={1}
								min={0.125}
								placeholder="0.125"
								step={0.125}
								type="number"
								value={bleed}
								onChange={(e) => setAmount('bleed', e.target.value)}
							/>

							<Form.Range
								aria-label="Bleed Size"
								className="mt-3"
								max={1}
								min={0.125}
								step={0.125}
								value={bleed}
								onChange={(e) => setAmount('bleed', e.target.value)}
							/>
						</Form.Group>
					)}
				</Col>
			</Row>
		</Form>
	);
};

export default ViewOptions;
